import { useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const getScroller = (scope) => scope?.closest?.('.page_content') || undefined;

const getElements = (selector, scope) => gsap.utils.toArray(selector, scope).filter(Boolean);

const getRevealOffset = (direction = 'up') => {
  const distance = 46;

  if (direction === 'left') return { x: -distance, y: 0 };
  if (direction === 'right') return { x: distance, y: 0 };
  if (direction === 'down') return { x: 0, y: -distance };

  return { x: 0, y: distance };
};

const scrollTriggerFor = (trigger, scroller, start = 'top 88%') => ({
  trigger,
  scroller,
  start,
  end: 'bottom top',
  toggleActions: 'play none none reverse',
});

export const useGsapAnimations = (scopeRef, refreshKey = '') => {
  useLayoutEffect(() => {
    const scope = scopeRef.current;

    if (!scope || prefersReducedMotion()) return undefined;

    const ctx = gsap.context(() => {
      const scroller = getScroller(scope);

      const introItems = getElements('[data-gsap-intro]', scope);
      if (introItems.length) {
        gsap.from(introItems, {
          autoAlpha: 0,
          y: 34,
          scale: 0.98,
          filter: 'blur(10px)',
          duration: 0.9,
          ease: 'power3.out',
          stagger: 0.08,
          clearProps: 'transform,opacity,visibility,filter',
        });
      }

      getElements('[data-gsap-reveal]', scope).forEach((item) => {
        const offset = getRevealOffset(item.dataset.gsapDirection);

        gsap.from(item, {
          autoAlpha: 0,
          x: offset.x,
          y: offset.y,
          scale: Number(item.dataset.gsapScale || 0.96),
          filter: 'blur(12px)',
          duration: Number(item.dataset.gsapDuration || 0.9),
          ease: 'power3.out',
          clearProps: 'transform,opacity,visibility,filter',
          scrollTrigger: scrollTriggerFor(item, scroller, item.dataset.gsapStart),
        });
      });

      getElements('[data-gsap-stagger]', scope).forEach((group) => {
        const targetSelector = group.getAttribute('data-gsap-stagger');
        const targets =
          !targetSelector || targetSelector === 'children'
            ? Array.from(group.children)
            : getElements(targetSelector, group);

        if (!targets.length) return;

        const offset = getRevealOffset(group.dataset.gsapDirection);

        gsap.from(targets, {
          autoAlpha: 0,
          x: offset.x * 0.65,
          y: offset.y * 0.65,
          scale: Number(group.dataset.gsapScale || 0.94),
          filter: 'blur(10px)',
          duration: Number(group.dataset.gsapDuration || 0.75),
          ease: group.dataset.gsapEase || 'back.out(1.35)',
          stagger: {
            each: Number(group.dataset.gsapEach || 0.08),
            from: group.dataset.gsapFrom || 'start',
          },
          clearProps: 'transform,opacity,visibility,filter',
          scrollTrigger: scrollTriggerFor(group, scroller, group.dataset.gsapStart || 'top 92%'),
        });
      });

      getElements('[data-gsap-float]', scope).forEach((item, index) => {
        gsap.to(item, {
          y: index % 2 === 0 ? -12 : 12,
          rotate: index % 2 === 0 ? 1.4 : -1.4,
          duration: 2.8 + index * 0.25,
          delay: 0.9 + index * 0.08,
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true,
        });
      });

      getElements('[data-gsap-pulse]', scope).forEach((item) => {
        gsap.to(item, {
          scale: 1.22,
          autoAlpha: 0.62,
          transformOrigin: '50% 50%',
          duration: 0.9,
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true,
        });
      });

      getElements('[data-gsap-scrub]', scope).forEach((item) => {
        gsap.fromTo(
          item,
          { yPercent: -8 },
          {
            yPercent: 8,
            ease: 'none',
            scrollTrigger: {
              trigger: item,
              scroller,
              start: 'top bottom',
              end: 'bottom top',
              scrub: 0.7,
            },
          }
        );
      });
    }, scope);

    const refreshId = window.setTimeout(() => ScrollTrigger.refresh(), 0);

    return () => {
      window.clearTimeout(refreshId);
      ctx.revert();
    };
  }, [scopeRef, refreshKey]);
};

export const useGsapChrome = (scopeRef, refreshKey = '') => {
  useLayoutEffect(() => {
    const scope = scopeRef.current;

    if (!scope || prefersReducedMotion()) return undefined;

    const ctx = gsap.context(() => {
      const timeline = gsap.timeline({ defaults: { ease: 'power3.out' } });

      timeline
        .from('.blurredg', {
          autoAlpha: 0,
          scale: 1.18,
          rotate: 8,
          duration: 1.1,
          clearProps: 'transform,opacity,visibility',
        })
        .from(
          '.top_nav',
          {
            autoAlpha: 0,
            y: -24,
            duration: 0.65,
            clearProps: 'transform,opacity,visibility',
          },
          0.05
        )
        .from(
          '.bottom_nav_wrapper',
          {
            autoAlpha: 0,
            y: 44,
            scale: 0.96,
            duration: 0.8,
            clearProps: 'transform,opacity,visibility',
          },
          0.12
        )
        .from(
          '.top_logo, .top_icon_btn',
          {
            autoAlpha: 0,
            y: -10,
            scale: 0.84,
            duration: 0.48,
            stagger: 0.06,
            clearProps: 'transform,opacity,visibility',
          },
          0.18
        )
        .from(
          '.nav_icon, .plus_btn',
          {
            autoAlpha: 0,
            y: 18,
            scale: 0.82,
            duration: 0.56,
            stagger: {
              each: 0.05,
              from: 'center',
            },
            clearProps: 'transform,opacity,visibility',
          },
          0.22
        );

      gsap.to('.active_nav img, .active_plus img', {
        y: -2,
        scale: 1.08,
        duration: 1.2,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
      });
    }, scope);

    return () => ctx.revert();
  }, [scopeRef, refreshKey]);
};

export { gsap, ScrollTrigger };
